import React, { useState, useMemo, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMutation } from '@apollo/client';
import {
  ADD_PARTICIPANT,
  BIND_PARTICIPANT_TO_ROLE,
  ASSIGN_STEP,
  CREATE_REQUEST_THREAD,
  DELETE_STEP_REQUEST_THREAD,
  GET_PLAN,
} from '../../graphql/queries';
import { Participant, PlanRole, PlanStep, RequestThreadStatus } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import GravatarAvatar from '../GravatarAvatar';
import PersonPicker, { PersonSelection } from './PersonPicker';
import { generateFieldsFromStep } from '../../utils/step-field-generator';

export type SheetMode = 'role' | 'step' | 'delegate';
export type DelegateChannel = 'email' | 'link' | 'sms';

// Feature flags
const FEATURE_FLAGS = {
  SMS_DELEGATION: false,
};

export interface DelegatedRequestThreadDraft {
  channel: DelegateChannel;
  recipientEmail?: string;
  recipientName?: string;
  stepId: string;
  stepTitle: string;
  createdAt: Date;
}

interface AssignOrRequestSheetProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  mode: SheetMode;
  role?: PlanRole | null;
  step?: PlanStep | null;
  participants: Participant[];
  roles?: PlanRole[];
  /** Pre-select a participant when opening */
  preselectedParticipantId?: string | null;
  onComplete?: () => void;
  onDelegationCreated?: (draft: DelegatedRequestThreadDraft) => void;
}

// Get status display info for waiting state
const getStatusInfo = (status: RequestThreadStatus) => {
  switch (status) {
    case RequestThreadStatus.Satisfied:
      return { label: 'Completed', color: colors.green[600], Icon: CheckCircleIcon };
    case RequestThreadStatus.Ready:
      return { label: 'Ready', color: colors.blue[600], Icon: CheckCircleIcon };
    case RequestThreadStatus.Open:
      return { label: 'Pending', color: colors.amber[600], Icon: PendingIcon };
    default:
      return { label: status, color: colors.gray[500], Icon: PendingIcon };
  }
};

/**
 * AssignOrRequestSheet - Unified sheet for role assignment and delegation
 *
 * Shared skeleton with:
 * - PersonPicker at top (always)
 * - Bottom slot: role assignment options OR delegation composer
 * - Non-blocking drawer (reduced scrim)
 * - Sticky footer with context-specific CTA
 */
export const AssignOrRequestSheet: React.FC<AssignOrRequestSheetProps> = ({
  open,
  onClose,
  planId,
  mode,
  role,
  step,
  participants,
  roles = [],
  preselectedParticipantId,
  onComplete,
  onDelegationCreated,
}) => {
  const { user } = useAuth();

  // Selection state
  const [selection, setSelection] = useState<PersonSelection | null>(null);

  // Role assignment options
  const [applyToAllSteps, setApplyToAllSteps] = useState(false);

  // Delegation options
  const [delegateChannel, setDelegateChannel] = useState<DelegateChannel>('email');

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Check for active delegation thread
  const activeThread = useMemo(() => {
    if (mode !== 'delegate' || !step?.requestThreads) return null;
    return step.requestThreads.find(
      (t) => t.status === RequestThreadStatus.Open || t.status === RequestThreadStatus.Ready
    );
  }, [mode, step]);

  // Find the delegate participant for active thread
  const activeThreadDelegate = useMemo(() => {
    if (!activeThread || !step?.assignedToId) return null;
    return participants.find((p) => p.id === step.assignedToId);
  }, [activeThread, step?.assignedToId, participants]);

  // Current user participant (prefer firebase uid, fallback email).
  const meParticipant = useMemo(() => {
    if (!user) return null;

    const byUid =
      user.uid
        ? participants.find((p) => p.firebaseUid && p.firebaseUid === user.uid)
        : null;
    if (byUid) return byUid;

    const normalizedEmail = user.email?.trim().toLowerCase();
    if (!normalizedEmail) return null;

    return participants.find((p) => p.email?.trim().toLowerCase() === normalizedEmail) || null;
  }, [participants, user]);

  // Pre-select participant when opening
  useEffect(() => {
    if (open && preselectedParticipantId) {
      const participant = participants.find((p) => p.id === preselectedParticipantId);
      if (participant) {
        setSelection({ type: 'existing', participant });
      }
    }
  }, [open, preselectedParticipantId, participants]);

  // Reset state when closing
  const handleReset = () => {
    setSelection(null);
    setApplyToAllSteps(false);
    setDelegateChannel('email');
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Mutations
  const [addParticipant, { loading: addingParticipant }] = useMutation(ADD_PARTICIPANT, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
  });

  const [bindParticipantToRole, { loading: bindingRole }] = useMutation(BIND_PARTICIPANT_TO_ROLE, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
  });

  const [assignStep, { loading: assigningStep }] = useMutation(ASSIGN_STEP, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
  });

  const [createThread, { loading: creatingThread }] = useMutation(CREATE_REQUEST_THREAD, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
  });

  const [deleteThread, { loading: deletingThread }] = useMutation(DELETE_STEP_REQUEST_THREAD, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
  });

  const loading = addingParticipant || bindingRole || assigningStep || creatingThread || deletingThread;

  // Validation
  const canSubmit = useMemo(() => {
    // In waiting state, no submission needed
    if (activeThread) return false;

    // Link mode doesn't need selection
    if (mode === 'delegate' && delegateChannel === 'link') return true;

    // Must have a selection
    if (!selection) return false;

    // Existing participant - always valid
    if (selection.type === 'existing') return true;

    // Invite - email must be valid (already validated by PersonPicker)
    if (selection.type === 'invite') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selection.email);
    }

    return false;
  }, [selection, mode, delegateChannel, activeThread]);

  // Get helper text for disabled state
  const getHelperText = () => {
    if (activeThread) return null;
    if (mode === 'delegate' && delegateChannel === 'link') return null;
    if (!selection) {
      return 'Select a person or enter a valid email to continue.';
    }
    return null;
  };

  const resolveParticipantForSelection = async (targetSelection: PersonSelection | null) => {
    if (!targetSelection) return null;

    if (targetSelection.type === 'existing') {
      return targetSelection.participant;
    }

    const { data } = await addParticipant({
      variables: {
        planId,
        participant: {
          name: targetSelection.name || targetSelection.email.split('@')[0],
          email: targetSelection.email,
          phone: null,
        },
      },
    });

    const newParticipant = data?.addParticipant?.participants?.find(
      (p: Participant) => p.email?.toLowerCase() === targetSelection.email.toLowerCase()
    );

    if (!newParticipant) {
      throw new Error('Failed to create participant');
    }

    return newParticipant;
  };

  // Handle form submission
  const handleSubmitSelection = async (selectionOverride?: PersonSelection | null) => {
    setError(null);

    try {
      const targetSelection = selectionOverride ?? selection;

      if (mode === 'role' && role) {
        // Role assignment
        const participant = await resolveParticipantForSelection(targetSelection);
        if (!participant) {
          return;
        }

        await bindParticipantToRole({
          variables: {
            planId,
            roleId: role.roleId,
            personId: participant.id,
          },
        });

        handleClose();
        onComplete?.();
      } else if (mode === 'step' && step) {
        // Step assignment
        const participant = await resolveParticipantForSelection(targetSelection);
        if (!participant) {
          return;
        }

        await assignStep({
          variables: {
            planId,
            stepId: step.id,
            participantId: participant.id,
          },
        });

        handleClose();
        onComplete?.();
      } else if (mode === 'delegate' && step) {
        // Delegation: prefer step.allowedFields when present (canonical step form)
        const generated = generateFieldsFromStep(step.title, step.description);
        const useStepFields = step.allowedFields && step.allowedFields.length > 0;
        const fields = useStepFields
          ? step.allowedFields!.map((f) => ({
              key: f.key,
              label: f.label,
              type: f.type,
              required: f.required,
              description: f.description ?? undefined,
              enumValues: f.enumValues ?? undefined,
            }))
          : generated.fields;
        const outputContract = useStepFields
          ? { allowedKeys: step.allowedFields!.map((f) => f.key) }
          : generated.outputContract;
        const uiMode = useStepFields ? ('form' as const) : generated.uiMode;

        if (delegateChannel === 'link') {
          // Link mode - no recipient needed
          await createThread({
            variables: {
              input: {
                parentPlanId: planId,
                parentStepId: step.id,
                requestSpec: {
                  ask: step.title,
                  description: step.description || `Please confirm when you have completed: ${step.title}`,
                  allowedFields: fields,
                  outputContract,
                  uiMode,
                },
                accessMode: 'PUBLIC',
                delegateTo: null,
              },
            },
          });

          const draft: DelegatedRequestThreadDraft = {
            channel: 'link',
            stepId: step.id,
            stepTitle: step.title,
            createdAt: new Date(),
          };

          onDelegationCreated?.(draft);
        } else {
          // Email delegation
          let contact: string;
          let recipientName: string;

          if (selection?.type === 'existing') {
            contact = selection.participant.email || '';
            recipientName = selection.participant.name;
          } else if (selection?.type === 'invite') {
            contact = selection.email;
            recipientName = selection.name || selection.email.split('@')[0];
          } else {
            return;
          }

          await createThread({
            variables: {
              input: {
                parentPlanId: planId,
                parentStepId: step.id,
                requestSpec: {
                  ask: step.title,
                  description: step.description || `Please confirm when you have completed: ${step.title}`,
                  allowedFields: fields,
                  outputContract,
                  uiMode,
                },
                accessMode: 'PUBLIC',
                delegateTo: {
                  method: delegateChannel === 'sms' ? 'sms' : 'email',
                  contact,
                },
              },
            },
          });

          const draft: DelegatedRequestThreadDraft = {
            channel: delegateChannel,
            recipientEmail: contact,
            recipientName,
            stepId: step.id,
            stepTitle: step.title,
            createdAt: new Date(),
          };

          onDelegationCreated?.(draft);
        }

        handleClose();
        onComplete?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    }
  };

  const handleAssignMyself = async () => {
    setError(null);

    try {
      if (!(mode === 'role' || mode === 'step')) return;
      if (!user) {
        setError('Please sign in to assign yourself.');
        return;
      }

      // Self-assignment uses the same participant assignment path as manual selection.
      // This is only a UI shortcut and does not bypass backend authorization checks.
      let targetParticipant: Participant | null = meParticipant;
      if (!targetParticipant) {
        const email = user.email?.trim();
        if (!email) {
          setError('We could not find your email. Please choose a participant manually.');
          return;
        }

        const defaultName =
          user.displayName?.trim() || email.split('@')[0] || 'Me';
        const { data } = await addParticipant({
          variables: {
            planId,
            participant: {
              name: defaultName,
              email,
              phone: null,
            },
          },
        });

        targetParticipant =
          data?.addParticipant?.participants?.find(
            (p: Participant) => p.email?.toLowerCase() === email.toLowerCase()
          ) || null;

        if (!targetParticipant) {
          setError('Could not create your participant profile. Please try again.');
          return;
        }
      }

      const selfSelection: PersonSelection = {
        type: 'existing',
        participant: targetParticipant,
      };
      setSelection(selfSelection);
      await handleSubmitSelection(selfSelection);
    } catch (err: any) {
      setError(err.message || 'Could not assign yourself');
    }
  };

  // Handle canceling active delegation
  const handleCancelDelegation = async () => {
    if (!activeThread || !step) return;
    if (!window.confirm('Cancel this delegation? The request will be removed.')) return;

    try {
      await deleteThread({
        variables: {
          planId,
          stepId: step.id,
          threadId: activeThread.threadId,
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to cancel delegation');
    }
  };

  // Get title based on mode
  const getTitle = () => {
    if (activeThread) return 'Delegation Active';
    switch (mode) {
      case 'role':
        return `Assign ${role?.name || 'Role'}`;
      case 'step':
        return 'Assign Step';
      case 'delegate':
        return 'Send Request';
    }
  };

  // Get subtitle based on mode
  const getSubtitle = () => {
    if (activeThread) return step?.title || 'Waiting for response';
    switch (mode) {
      case 'role':
        return role?.description || `Who will be the ${role?.name}?`;
      case 'step':
        return step?.title || 'Select who should complete this step';
      case 'delegate':
        return step?.title || 'Send this task to someone';
    }
  };

  // Get accent color
  const getAccentColor = () => {
    if (activeThread) return colors.amber[500];
    switch (mode) {
      case 'delegate':
        return colors.amber[500];
      default:
        return colors.blue[600];
    }
  };

  // Get CTA label
  const getCtaLabel = () => {
    if (loading) return 'Processing...';

    switch (mode) {
      case 'role':
      case 'step':
        return selection?.type === 'invite' ? 'Invite & Assign' : 'Assign';
      case 'delegate':
        if (delegateChannel === 'link') return 'Create Link';
        return 'Send Request';
    }
  };

  const helperText = getHelperText();
  const accentColor = getAccentColor();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      // Reduced scrim for non-blocking feel
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          maxWidth: 420,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 2.5,
          pb: 2,
          borderBottom: `1px solid ${colors.gray[100]}`,
        }}
      >
        <Box sx={{ flex: 1, pr: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.textPrimary,
              fontSize: '1.1rem',
            }}
          >
            {getTitle()}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              mt: 0.25,
              lineHeight: 1.4,
            }}
          >
            {getSubtitle()}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: colors.gray[400],
            '&:hover': { color: colors.gray[600] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* WAITING STATE - Show when there's an active delegation */}
        {activeThread ? (
          <Box>
            {/* Delegate info */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                bgcolor: colors.amber[50],
                borderRadius: 2,
                border: `1px solid ${colors.amber[200]}`,
                mb: 2,
              }}
            >
              {activeThreadDelegate ? (
                <GravatarAvatar
                  email={activeThreadDelegate.email}
                  name={activeThreadDelegate.name}
                  size={44}
                  showTooltip={false}
                />
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: colors.amber[200],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PendingIcon sx={{ color: colors.amber[700] }} />
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {activeThreadDelegate?.name || 'Delegate'}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {activeThreadDelegate?.email || 'Waiting for response'}
                </Typography>
              </Box>
              <Chip
                icon={React.createElement(getStatusInfo(activeThread.status).Icon, {
                  sx: { fontSize: 14 },
                })}
                label={getStatusInfo(activeThread.status).label}
                size="small"
                sx={{
                  height: 24,
                  bgcolor: `${getStatusInfo(activeThread.status).color}15`,
                  color: getStatusInfo(activeThread.status).color,
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: getStatusInfo(activeThread.status).color,
                  },
                }}
              />
            </Box>

            {/* Sent time */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                color: colors.textSecondary,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                Sent {activeThread.createdAt ? new Date(activeThread.createdAt).toLocaleDateString() : 'recently'}
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: colors.gray[300],
                  color: colors.textPrimary,
                  justifyContent: 'flex-start',
                  py: 1.25,
                }}
              >
                View request
              </Button>
              <Button
                variant="outlined"
                startIcon={<NotificationsIcon />}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: colors.gray[300],
                  color: colors.textPrimary,
                  justifyContent: 'flex-start',
                  py: 1.25,
                }}
              >
                Send reminder
              </Button>
              <Button
                variant="outlined"
                startIcon={deletingThread ? <CircularProgress size={16} /> : <CancelIcon />}
                fullWidth
                onClick={handleCancelDelegation}
                disabled={deletingThread}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.red[100],
                  color: colors.red[600],
                  justifyContent: 'flex-start',
                  py: 1.25,
                  '&:hover': {
                    borderColor: colors.red[600],
                    bgcolor: colors.red[50],
                  },
                }}
              >
                Cancel delegation
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {(mode === 'role' || mode === 'step') && user && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleAssignMyself}
                disabled={loading}
                startIcon={<PersonIcon sx={{ fontSize: 18 }} />}
                sx={{
                  mb: 1.5,
                  textTransform: 'none',
                  borderColor: accentColor,
                  color: accentColor,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: accentColor,
                    bgcolor: `${accentColor}08`,
                  },
                }}
              >
                Assign myself
              </Button>
            )}

            {/* PERSON PICKER - Always shown for role/step assignment */}
            {!(mode === 'delegate' && delegateChannel === 'link') && (
              <PersonPicker
                participants={participants}
                currentUserEmail={user?.email}
                selection={selection}
                onSelect={setSelection}
                showMeOption={mode !== 'delegate'}
                placeholder="Search name or email"
                accentColor={accentColor}
              />
            )}

            {/* BOTTOM SLOT - Mode-specific options */}
            <Box sx={{ mt: 3 }}>
              {/* Role assignment options */}
              {mode === 'role' && role && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: colors.gray[50],
                    borderRadius: 2,
                    border: `1px solid ${colors.gray[200]}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: colors.textSecondary, display: 'block', mb: 1 }}
                  >
                    Role: {role.name}
                  </Typography>
                  {role.description && (
                    <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
                      {role.description}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyToAllSteps}
                        onChange={(e) => setApplyToAllSteps(e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: accentColor,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: accentColor,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        Apply to all steps for this role
                      </Typography>
                    }
                  />
                </Box>
              )}

              {/* Delegation channel selector */}
              {mode === 'delegate' && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: colors.textSecondary, display: 'block', mb: 1.5 }}
                  >
                    How to send
                  </Typography>
                  <ToggleButtonGroup
                    value={delegateChannel}
                    exclusive
                    onChange={(_, value) => value && setDelegateChannel(value)}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        py: 1.25,
                        textTransform: 'none',
                        fontWeight: 500,
                        '&.Mui-selected': {
                          bgcolor: `${accentColor}12`,
                          color: accentColor,
                          borderColor: accentColor,
                          '&:hover': {
                            bgcolor: `${accentColor}20`,
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="email">
                      <EmailIcon sx={{ mr: 0.75, fontSize: 18 }} />
                      Email
                    </ToggleButton>
                    <ToggleButton value="link">
                      <LinkIcon sx={{ mr: 0.75, fontSize: 18 }} />
                      Link
                    </ToggleButton>
                    {FEATURE_FLAGS.SMS_DELEGATION && (
                      <ToggleButton value="sms">
                        <SmsIcon sx={{ mr: 0.75, fontSize: 18 }} />
                        SMS
                      </ToggleButton>
                    )}
                  </ToggleButtonGroup>

                  {delegateChannel === 'link' && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: colors.amber[50],
                        borderRadius: 2,
                        border: `1px solid ${colors.amber[200]}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.amber[700] }}>
                        A shareable link will be created. Anyone with the link can respond.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Footer - Only show when not in waiting state */}
      {!activeThread && (
        <Box
          sx={{
            p: 2.5,
            pt: 2,
            borderTop: `1px solid ${colors.gray[100]}`,
            bgcolor: 'white',
          }}
        >
          {/* Helper text when CTA is disabled */}
          {helperText && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: colors.textSecondary,
                mb: 1.5,
                textAlign: 'center',
              }}
            >
              {helperText}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                flex: 1,
                color: colors.gray[600],
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void handleSubmitSelection();
              }}
              disabled={loading || !canSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : mode === 'delegate' ? (
                  delegateChannel === 'link' ? (
                    <LinkIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )
                ) : undefined
              }
              sx={{
                flex: 2,
                bgcolor: accentColor,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: mode === 'delegate' ? colors.amber[600] : colors.blue[700],
                },
                '&.Mui-disabled': {
                  bgcolor: colors.gray[200],
                  color: colors.gray[500],
                },
              }}
            >
              {getCtaLabel()}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default AssignOrRequestSheet;
